import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  bookAppointment,
  getActiveVirtualAppointments,
  getAvailableSlots,
  getBookableBusinesses,
  getBookableDoctors,
  getMyAppointments,
  payAppointment,
  startVideoCall,
  type AppointmentSlotsParams,
} from "@/features/appointments/api/appointmentsApi";
import type { AppointmentCreatePayload } from "@/features/appointments/types";
import { invalidateWalletQueries } from "@/features/wallet/hooks/useWallet";

export const appointmentKeys = {
  root: ["appointments"] as const,
  mine: ["appointments", "mine"] as const,
  businesses: ["appointments", "businesses"] as const,
  doctors: (businessId: number | null, serviceId: number | null = null) => ["appointments", "doctors", businessId, serviceId] as const,
  slots: (businessId: number | null, staffUserId: number | null, forDate: string | null) =>
    ["appointments", "slots", businessId, staffUserId, forDate] as const,
  dayAvailability: (businessId: number | null, staffUserId: number | null, forDate: string) =>
    ["appointments", "day-availability", businessId, staffUserId, forDate] as const,
};

/**
 * Merr terminet personale per pacientin ose mjekun aktiv.
 */
export const useMyAppointments = (enabled = true) =>
  useQuery({
    queryKey: appointmentKeys.mine,
    queryFn: getMyAppointments,
    enabled,
  });

/**
 * Merr business-et publike qe ofrojne booking.
 */
export const useAppointmentBusinesses = () =>
  useQuery({
    queryKey: appointmentKeys.businesses,
    queryFn: getBookableBusinesses,
    staleTime: 5 * 60 * 1000,
  });

/**
 * Merr mjeket e rezervueshem per nje business.
 */
export const useAppointmentDoctors = (businessId: number | null, serviceId: number | null = null) =>
  useQuery({
    queryKey: appointmentKeys.doctors(businessId, serviceId),
    queryFn: () => getBookableDoctors(businessId ?? 0, serviceId),
    // service_id është opsionale në backend — mjekët ngarkohen sapo business zgjidhet
    enabled: businessId !== null,
    staleTime: 5 * 60 * 1000,
  });

/**
 * Merr slot-et e lira per nje date te zgjedhur.
 */
export const useAppointmentSlots = (params: AppointmentSlotsParams | null) =>
  useQuery({
    queryKey: appointmentKeys.slots(params?.business_id ?? null, params?.staff_user_id ?? null, params?.for_date ?? null),
    queryFn: () => getAvailableSlots(params ?? { business_id: 0, staff_user_id: 0, for_date: "" }),
    enabled: params !== null,
    staleTime: 60 * 1000,
  });

/** Terminet virtuale ±30 min — polling 30s për badge LIVE. */
export const useActiveVirtualAppointments = (enabled = true) =>
  useQuery({
    queryKey: ["appointments", "active-virtual"] as const,
    queryFn:  getActiveVirtualAppointments,
    enabled,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

/** Mjeku nis thirrjen video — gjeneron linkun Jitsi. */
export const useStartVideoCall = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (appointmentId: number) => startVideoCall(appointmentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["appointments", "active-virtual"] });
      void queryClient.invalidateQueries({ queryKey: appointmentKeys.mine });
    },
  });
};

/**
 * Krijon dhe paguan terminin si nje zinxhir UI pa optimistic update.
 */
export const useBookAndPayAppointment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: AppointmentCreatePayload) => {
      const appointment = await bookAppointment(payload);
      const payment = await payAppointment(appointment.id);
      return {
        appointment: {
          ...appointment,
          status: "confirmed" as const,
        },
        payment,
      };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: appointmentKeys.mine });
      void queryClient.invalidateQueries({ queryKey: appointmentKeys.root });
      invalidateWalletQueries(queryClient);
    },
  });
};

/** Krijon termin nga workspace i profesionistit pa kaluar ne flow-in e pageses se pacientit. */
export const useCreateProfessionalAppointment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: AppointmentCreatePayload) => bookAppointment(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: appointmentKeys.mine });
      void queryClient.invalidateQueries({ queryKey: appointmentKeys.root });
    },
  });
};
